/**
 * Authentication Provider
 * Manages user authentication state via Supabase
 */

import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { supabase, isSupabaseConfigured } from '@/config/supabase';
import { getCurrentUser, type AuthUser } from '@/services/auth';
import { getProfile } from '@/services/profileService';
import type { UserProfile } from '@/types/profile.types';
import type { Session } from '@supabase/supabase-js';
import { AuthContext, type AuthContextValue } from './auth-context';

export interface AuthProviderProps {
  children: ReactNode;
}

const AUTH_TIMEOUT_MS = 8000;

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isConfigured = useMemo(() => isSupabaseConfigured(), []);
  const profileFetchRef = useRef(0);
  const isInitializedRef = useRef(false);
  const authResolvedRef = useRef(false);

  const refreshUser = useCallback(async () => {
    if (!isConfigured) {
      setUser(null);
      setSession(null);
      setIsLoading(false);
      return;
    }

    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
    }
  }, [isConfigured]);

  // Initialize auth state
  useEffect(() => {
    if (!isConfigured) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    // Check if we're returning from an OAuth redirect
    const url = new URL(window.location.href);
    const hasOAuthCode = url.searchParams.has('code');

    async function initAuth() {
      try {
        // If we have an OAuth code, let onAuthStateChange handle the session
        // Don't call getSession() yet as the code exchange is async
        if (hasOAuthCode) {
          return; // onAuthStateChange will set isLoading = false
        }

        // Get initial session (only when not returning from OAuth)
        const { data } = await supabase.auth.getSession();

        if (!mounted) return;

        setSession(data.session);

        if (data.session?.user) {
          const currentUser = await getCurrentUser();
          if (mounted) {
            setUser(currentUser);
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
      } finally {
        // Only set loading false if NOT waiting for OAuth code exchange
        if (mounted && !hasOAuthCode) {
          authResolvedRef.current = true;
          setIsLoading(false);
        }
      }
    }

    // Set up auth listener BEFORE calling initAuth
    // This ensures we catch the SIGNED_IN event from OAuth code exchange
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        // Only log significant auth events (not token refreshes)
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
          console.warn('[AuthContext] Auth state changed:', event);
        }

        // Clean up OAuth code from URL after successful sign-in
        // The code parameter should be removed after Supabase exchanges it for a session
        if (event === 'SIGNED_IN') {
          const url = new URL(window.location.href);
          if (url.searchParams.has('code')) {
            url.searchParams.delete('code');
            window.history.replaceState({}, document.title, url.pathname + url.search);
          }
        }

        if (!mounted) return;

        // Use flushSync to force synchronous state updates
        // This ensures React processes the auth state change immediately
        // instead of batching it, which can cause the UI to not update
        // when the callback is triggered from Supabase's event system
        flushSync(() => {
          setSession(currentSession);

          if (currentSession?.user) {
            // Set user immediately from session to avoid race conditions
            // The session already contains user data, no need to fetch again
            const sessionUser = currentSession.user;
            const authUser: AuthUser = {
              id: sessionUser.id,
              email: sessionUser.email || '',
              createdAt: new Date(sessionUser.created_at || Date.now()),
              shippingAddress: sessionUser.user_metadata?.shipping_address as string | undefined,
            };
            setUser(authUser);
          } else {
            setUser(null);
          }

          authResolvedRef.current = true;
          setIsLoading(false);
        });
      }
    );

    // Safety timeout: if auth hasn't resolved in 8s, stop loading
    // This covers OAuth code exchange hangs and slow getSession() calls
    const authTimeout = setTimeout(() => {
      if (mounted && !authResolvedRef.current) {
        console.warn(
          '[AuthContext] Auth initialization timed out after 8s — forcing loading to false'
        );
        authResolvedRef.current = true;
        setIsLoading(false);
        isInitializedRef.current = true;
      }
    }, AUTH_TIMEOUT_MS);

    // Call initAuth AFTER listener is set up
    initAuth().finally(() => {
      if (mounted) {
        isInitializedRef.current = true;
      }
    });

    return () => {
      mounted = false;
      clearTimeout(authTimeout);
      authListener.subscription.unsubscribe();
    };
  }, [isConfigured]);

  // Proactive token refresh when user returns to the app
  // This ensures tokens are fresh after being away (like Amazon/Google behavior)
  useEffect(() => {
    if (!isConfigured) return;

    async function handleVisibilityChange() {
      // Don't refresh during initial auth — prevents race conditions
      if (!isInitializedRef.current) return;
      if (document.visibilityState === 'visible') {
        try {
          // Refresh session when user returns to the tab
          const { data, error } = await supabase.auth.refreshSession();
          if (error) {
            // Session expired or invalid - user needs to re-login
            console.warn('[AuthContext] Session refresh failed:', error.message);
          } else if (data.session) {
            setSession(data.session);
            const currentUser = await getCurrentUser();
            setUser(currentUser);
          }
        } catch (error) {
          console.error('[AuthContext] Error refreshing session on visibility:', error);
        }
      }
    }

    // Also refresh on window focus (covers more cases than visibility alone)
    async function handleWindowFocus() {
      // Don't refresh during initial auth — prevents race conditions
      if (!isInitializedRef.current) return;
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          setSession(data.session);
        }
      } catch (error) {
        console.error('[AuthContext] Error checking session on focus:', error);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [isConfigured]);

  // Fetch profile non-blocking when user changes
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }

    const fetchId = ++profileFetchRef.current;

    getProfile(user.id)
      .then((data) => {
        if (fetchId === profileFetchRef.current) {
          setProfile(data);
        }
      })
      .catch((err) => {
        console.error('[AuthContext] Failed to fetch profile:', err);
      });
  }, [user]);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      session,
      profile,
      isLoading,
      isConfigured,
      refreshUser,
    }),
    [user, session, profile, isLoading, isConfigured, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
